import { Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { PbjPrediction, PbjPredictionJobCoverageGap } from "../../../../utils/models";
import { ManualHoursButton } from "../ManualHoursButton";
import { MissingJobsWarning } from "../MissingJobsWarning";

type Props = {
	prediction: PbjPrediction;
	startDate: string;
	endDate: string;
	// Jobs with no hours in the window. `undefined` while the coverage query is still resolving, or
	// on the audit basis where the question does not apply.
	missingJobs: PbjPredictionJobCoverageGap[] | undefined;
};

/**
 * Setup step 3 — the hours the score is built from. It names the source (payroll punches via the
 * Audit, or the InHouse schedule), then shows which jobs put no hours into the window at all, which
 * is the one gap a facility can close here: salaried nurses — DON, ADON, MDS Coordinator — routinely
 * never punch, so their hours reach the calculation only as manual entries.
 */
export const SetupManualHoursStep: FC<Props> = ({ prediction, startDate, endDate, missingJobs }) => {
	const { t } = useTranslation();
	const hasMissingJobs = missingJobs !== undefined && missingJobs.length > 0;

	return (
		<Stack spacing={2}>
			<Typography variant="body2" color="text.secondary">
				{t(prediction.hoursSource === "audit" ? "pbj_model.setup.step.manual_hours.help_audit" : "pbj_model.setup.step.manual_hours.help_schedule")}
			</Typography>
			{hasMissingJobs && <MissingJobsWarning gaps={missingJobs} variant="detailed" dataTestId="pbj-model-setup-missing-jobs" />}
			<Typography variant="body2" color="text.secondary">
				{t(hasMissingJobs ? "pbj_model.setup.step.manual_hours.add_prompt" : "pbj_model.setup.step.manual_hours.no_gaps")}
			</Typography>
			<ManualHoursButton
				departmentId={prediction.config.departmentId}
				startDate={startDate}
				endDate={endDate}
				prediction={prediction}
				emphasized={hasMissingJobs}
			/>
		</Stack>
	);
};
